import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { COMPANY, LOGO, NAV_LINKS } from "@/lib/site";

export const Header = () => {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const assessmentTo = pathname === "/contact" ? "#enquiry-form" : "/#assessment";

    return (
        <header data-testid="site-header" className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
            <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link to="/" data-testid="header-logo-link" className="flex shrink-0 items-center" onClick={() => setOpen(false)}>
                    <img src={LOGO} alt="Virtex Renewables — Clean Energy for a Brighter Tomorrow" className="h-11 w-auto sm:h-12" />
                </Link>

                <nav className="hidden items-center gap-1 lg:flex" aria-label="Main navigation">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.to === "/"}
                            data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                            className={({ isActive }) =>
                                `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                                    isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                }`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden items-center gap-3 lg:flex">
                    <a
                        href={COMPANY.phoneHref}
                        data-testid="header-phone-link"
                        className="flex items-center gap-2 rounded-full px-3 py-2 text-sm font-bold text-slate-900 transition-colors duration-200 hover:text-emerald-700"
                    >
                        <Phone className="h-4 w-4 text-emerald-600" />
                        {COMPANY.phoneDisplay}
                    </a>
                    <Link
                        to={assessmentTo}
                        data-testid="header-assessment-cta"
                        className="group flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-600/20 transition-all duration-200 hover:bg-emerald-700"
                    >
                        Get a Free Assessment
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                </div>

                <button
                    onClick={() => setOpen(!open)}
                    data-testid="mobile-menu-button"
                    aria-label={open ? "Close menu" : "Open menu"}
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-slate-900 lg:hidden"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="mobile-menu"
                        className="overflow-hidden border-t border-slate-100 bg-white lg:hidden"
                    >
                        <nav className="space-y-1 px-4 py-4" aria-label="Mobile navigation">
                            {NAV_LINKS.map((link) => (
                                <NavLink
                                    key={link.to}
                                    to={link.to}
                                    end={link.to === "/"}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                                    className={({ isActive }) =>
                                        `block rounded-xl px-4 py-3 text-base font-semibold ${
                                            isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                                        }`
                                    }
                                >
                                    {link.label}
                                </NavLink>
                            ))}
                            <div className="space-y-2 pt-3">
                                <a
                                    href={COMPANY.phoneHref}
                                    data-testid="mobile-menu-phone-link"
                                    className="flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-3 text-sm font-bold text-slate-900"
                                >
                                    <Phone className="h-4 w-4 text-emerald-600" /> {COMPANY.phoneDisplay}
                                </a>
                                <Link
                                    to={assessmentTo}
                                    onClick={() => setOpen(false)}
                                    data-testid="mobile-menu-assessment-cta"
                                    className="flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white"
                                >
                                    Get a Free Assessment <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
